import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from '@discovr/common';
import type { ImportResult } from '@discovr/types';
import { campaignKeys } from '../queryKeys';

const HTTP_NO_CONTENT = 204;
const HTTP_FORBIDDEN = 403;

/**
 * Fetches the stored import result for a campaign.
 * Returns null when no import has been run yet (BE returns 204), or when the
 * caller's role isn't permitted to view import results (BE returns 403) — in
 * both cases there's simply no import banner to show. Shared by
 * `useLastImportResult` and the import wizard's status-check fallback.
 */
export async function fetchLastImportResult(campaignId: string): Promise<ImportResult | null> {
  try {
    const res = await apiClient.get<{ data: ImportResult }>(
      `/campaigns/${encodeURIComponent(campaignId)}/last-import-result`,
    );
    // Axios resolves 204 with res.data = '' — treat as null.
    if (res.status === HTTP_NO_CONTENT || !res.data?.data) return null;
    return res.data.data;
  } catch (err) {
    // 403 = this role can't see import results — degrade to "no import".
    if (err instanceof ApiError && err.httpStatus === HTTP_FORBIDDEN) return null;
    throw err;
  }
}

export function useLastImportResult(
  campaignId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<ImportResult | null>({
    queryKey: campaignKeys.lastImport(campaignId),
    queryFn: () => fetchLastImportResult(campaignId),
    // Import results are an admin-app-only capability: the endpoint requires the
    // roster:import scope, so brand callers only ever get a 403 (which
    // fetchLastImportResult maps to null). Callers pass enabled=false for roles that can't import so the brand
    // app never fires the guaranteed-403 request at all. Defaults to true to keep
    // existing callers unchanged.
    enabled: !!campaignId && (options?.enabled ?? true),
    staleTime: 5 * 60_000, // 5 min — result changes only on new upload
  });
}
